import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  ModalPortal,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import {
  fetchArticles,
  fetchZendeskTags,
  saveZendeskTags,
  createZendeskTag,
  recrawlZendeskArticle,
  fetchZendeskAvailableTags
} from '../api';
import { StateLevel } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import { useBulkSelection } from './useBulkSelection';
import { runBulkAction, describeBulkResult } from './bulkActionUtils';
import BulkActionsBar from './BulkActionsBar';
import TagModal from './TagModal';

const ZendeskArticlesList = ({
  categoryId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const order = useOrderableTableReducer({ id: `zendesk-${categoryId}` });
  const pagination = usePaginationReducer({ id: `zendesk-${categoryId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/zendesk/articles',
      categoryId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchArticles(categoryId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/zendesk/groups/available'],
    queryFn: fetchZendeskAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_zendesk_articles',
    createTagFn: createZendeskTag
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const articles = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const categoryName = response.category_name ?? 'Articles';
  const pageIds = useMemo(() => articles.map(article => article.id), [articles]);

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  const refetchArticles = () => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', categoryId] });
  };

  const findArticle = (id) => articles.find(article => article.id === id);

  const handleBulkRecrawl = () => {
    const ids = bulk.selectedIds;
    if (ids.length === 0) {
      return;
    }
    setBulkModal({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl ${ids.length} selected article${ids.length > 1 ? 's' : ''}?`}
          contentBody="This will update their content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(ids, recrawlZendeskArticle);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'recrawl requested', 'article')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request recrawl for ${result.failed.length} article${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchArticles();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findArticle(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected articles are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findArticle(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchZendeskTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveZendeskTags({ zendeskId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'article')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} article${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchArticles();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  return (
    <AnimatePresence>
      <PanelSlider header={categoryName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !articles.length && (
          <Paragraph mb secondary>No articles available.</Paragraph>
        )}
        {!isLoading && articles.length > 0 && (
          <Flex col>
            <PermissionFilter can="edit generative ai resource">
              <BulkActionsBar
                count={bulk.count}
                onClear={bulk.clear}
                actions={[
                  <PermissionFilter can="view generative ai tag" key="bulk-tag">
                    <PermissionFilter can="edit generative ai tag">
                      <Button
                        icon="tag"
                        variant="secondary"
                        onClick={openBulkTagsModal}
                        mr
                      >
                        Tags
                      </Button>
                    </PermissionFilter>
                  </PermissionFilter>,
                  <Button
                    mr
                    key="bulk-recrawl"
                    icon="download"
                    variant="secondary"
                    loading={bulkLoading}
                    onClick={handleBulkRecrawl}
                  >
                    Recrawl
                  </Button>
                ]}
              />
            </PermissionFilter>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      ...(canBulkSelect ? [{
                        label: (
                          <Checkbox
                            aria-label="Select all articles"
                            checked={bulk.isAllSelected(pageIds)}
                            onChange={() => bulk.toggleAll(pageIds)}
                          />
                        )
                      }] : []),
                      { label: 'Title', sort: 'title' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {articles.map(article => (
                    <TableRow key={article.id}>
                      {canBulkSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select article ${article.id}`}
                            checked={bulk.isSelected(article.id)}
                            onChange={() => bulk.toggle(article.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{article.title}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={article.status} />
                          <span>{article.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{article.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {article.groups && article.groups.length > 0
                            ? article.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {article.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(article)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            <Button
                              icon="download"
                              loading={Boolean(loadingIds?.[article.id])}
                              onClick={() => onOpenRecrawlModal(article)}
                            >
                              Recrawl
                            </Button>
                          </PermissionFilter>

                          {errorIds?.[article.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[article.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}

            <ModalPortal>
              <TagModal
                isVisible={isBulkTagsModalVisible}
                header={`Tag ${bulk.count} Articles`}
                onCancel={() => {
                  setIsBulkTagsModalVisible(false);
                  setSelectedTags([]);
                }}
                onConfirm={handleBulkTagConfirm}
                tags={tags}
                selectedTags={selectedTags}
                handleTagChange={handleTagChange}
              />
            </ModalPortal>

            {bulkModal && (
              <ModalPortal>
                <Modal
                  header={bulkModal.header}
                  body={bulkModal.body}
                  confirmText={bulkModal.confirmText}
                  cancelText="Cancel"
                  handleCancel={() => setBulkModal(null)}
                  handleConfirm={bulkModal.handleConfirm}
                  loading={bulkLoading}
                />
              </ModalPortal>
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ZendeskArticlesList.propTypes = {
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object.isRequired
};

export default ZendeskArticlesList;